const UPSELL_SELECTOR = '[data-blackroll-cart-upsell]';
const TRACK_SELECTOR = '[data-upsell-track]';
const ADD_SELECTOR = '[data-upsell-add]';
const VARIANT_SELECTOR = '[data-upsell-variant-select]';
const MAX_RECOMMENDATIONS = 3;

class BlackrollProductUpsell {
  constructor(element) {
    this.element = element;
    this.productId = element.dataset.referenceProductId;
    this.context = element.dataset.upsellContext || 'cart';
    this.track = element.querySelector(TRACK_SELECTOR);
    this.rootUrl = window.Shopify?.routes?.root || '/';
    this.currency = window.Shopify?.currency?.active || 'EUR';

    this.initialize();
  }

  async initialize() {
    if (!this.productId || !this.track) {
      this.remove();
      return;
    }

    try {
      const [recommendations, cart] = await Promise.all([
        this.fetchRecommendations(),
        this.fetchCart(),
      ]);

      this.currency = cart.currency || this.currency;

      const cartProductIds = new Set(
        (cart.items || []).map((item) => String(item.product_id))
      );

      const products = recommendations
        .filter((product) => {
          return (
            product.available &&
            !cartProductIds.has(String(product.id)) &&
            this.getAvailableVariants(product).length > 0
          );
        })
        .slice(0, MAX_RECOMMENDATIONS);

      if (!products.length) {
        this.remove();
        return;
      }

      this.renderProducts(products);
      this.bindEvents();
      this.element.hidden = false;
    } catch (error) {
      console.error('BLACKROLL complementary products error:', error);
      this.remove();
    }
  }

  async fetchRecommendations() {
    const url = new URL(
      `${this.rootUrl}recommendations/products.json`,
      window.location.origin
    );

    url.searchParams.set('product_id', this.productId);
    url.searchParams.set('limit', String(MAX_RECOMMENDATIONS));
    url.searchParams.set('intent', 'complementary');

    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error('Could not load complementary products.');
    }

    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  async fetchCart() {
    const response = await fetch(`${this.rootUrl}cart.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error('Could not retrieve the cart.');
    }

    return response.json();
  }

  getAvailableVariants(product) {
    if (!Array.isArray(product.variants)) {
      return [];
    }

    return product.variants.filter((variant) => variant.available);
  }

  hasSelectableVariants(product) {
    if (!Array.isArray(product.variants)) {
      return false;
    }

    if (product.variants.length > 1) {
      return true;
    }

    const firstVariant = product.variants[0];

    return Boolean(
      firstVariant &&
      firstVariant.title &&
      firstVariant.title !== 'Default Title'
    );
  }

  /*
    Product option names in the catalog are inconsistent (a lot of
    variant options are just named "Title", some size options carry
    literal dimensions, etc.), so instead of trusting product.options
    we look at the actual variant values to guess whether this is a
    color choice or a size choice, and label the placeholder option
    accordingly. Anything we can't confidently classify keeps the
    neutral "Επιλέξτε" label.
  */
  getVariantOptionLabel(product) {
    const COLOR_WORDS = [
      'black', 'white', 'red', 'blue', 'green', 'yellow', 'orange',
      'pink', 'purple', 'violet', 'grey', 'gray', 'brown', 'beige',
      'navy', 'azur', 'azure', 'silver', 'gold', 'khaki', 'maroon',
      'teal', 'turquoise', 'cream', 'ivory',
      'μαύρο', 'λευκό', 'κόκκινο', 'μπλε', 'πράσινο', 'κίτρινο',
      'πορτοκαλί', 'ροζ', 'μωβ', 'γκρι', 'καφέ', 'ασημί', 'χρυσό',
    ];

    const SIZE_WORDS = [
      'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '2xl', '3xl',
      'small', 'medium', 'large',
      'μικρό', 'μεσαίο', 'μεγάλο',
    ];

    const optionName = String(product.options?.[0] || '').toLowerCase();

    if (
      optionName.includes('χρώμα') ||
      optionName.includes('color') ||
      optionName.includes('colour')
    ) {
      return 'Επιλέξτε Χρώμα';
    }

    if (optionName.includes('μέγεθος') || optionName.includes('size')) {
      return 'Επιλέξτε Μέγεθος';
    }

    const values = (product.variants || [])
      .flatMap((variant) =>
        String(variant.option1 || variant.title || '')
          .toLowerCase()
          .split(/[\s/]+/)
      )
      .filter(Boolean);

    if (values.some((value) => COLOR_WORDS.includes(value))) {
      return 'Επιλέξτε Χρώμα';
    }

    if (
      values.some((value) => SIZE_WORDS.includes(value)) ||
      values.some((value) => /^\d+x\d+$/.test(value))
    ) {
      return 'Επιλέξτε Μέγεθος';
    }

    return 'Επιλέξτε';
  }

  getProductImage(product) {
    const image =
      product.featured_image ||
      product.images?.[0] ||
      product.featured_media?.preview_image?.src;

    if (!image) return '';
    if (typeof image === 'string') return image;

    return image.src || image.url || '';
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat('el-GR', {
        style: 'currency',
        currency: this.currency,
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} €`;
    }
  }

  escapeHtml(value = '') {
    const element = document.createElement('div');
    element.textContent = String(value);
    return element.innerHTML;
  }

  renderProducts(products) {
    const markup = products
      .map((product) => this.createProductMarkup(product))
      .filter(Boolean)
      .join('');

    if (!markup) {
      this.remove();
      return;
    }

    this.track.innerHTML = markup;
    this.track.setAttribute('aria-busy', 'false');
  }

  createProductMarkup(product) {
    const availableVariants = this.getAvailableVariants(product);

    if (!availableVariants.length) {
      return '';
    }

    const defaultVariant = availableVariants[0];
    const productTitle = this.escapeHtml(product.title);
    const productUrl = this.escapeHtml(product.url);
    const imageUrl = this.escapeHtml(this.getProductImage(product));
    const hasSelectableVariants = this.hasSelectableVariants(product);
    const variantOptionLabel = this.getVariantOptionLabel(product);

    const imageMarkup = imageUrl
      ? `
        <img
          class="blackroll-cart-upsell__image"
          src="${imageUrl}"
          alt="${productTitle}"
          width="124"
          height="124"
          loading="lazy"
        >
      `
      : `
        <div
          class="blackroll-cart-upsell__image-placeholder"
          aria-hidden="true"
        ></div>
      `;

    const variantOptions = availableVariants
      .map((variant) => {
        const title = this.escapeHtml(variant.title || '');
        const price = this.formatMoney(variant.price);

        return `
          <option
            value="${variant.id}"
            data-price="${variant.price}"
          >
            ${title} — ${price}
          </option>
        `;
      })
      .join('');

    const actionMarkup = hasSelectableVariants
      ? `
        <select
          class="blackroll-cart-upsell__variant-select"
          data-upsell-variant-select
          aria-label="Επιλογή παραλλαγής για ${productTitle}"
        >
          <option value="" selected disabled>
            ${variantOptionLabel}
          </option>
          ${variantOptions}
        </select>

        <button
          type="button"
          class="button blackroll-cart-upsell__add-button"
          data-upsell-add
          disabled
        >
          <span data-upsell-button-label>Προσθήκη</span>
        </button>
      `
      : `
        <button
          type="button"
          class="button blackroll-cart-upsell__add-button"
          data-upsell-add
          data-variant-id="${defaultVariant.id}"
        >
          <span data-upsell-button-label>Προσθήκη</span>
        </button>
      `;

    return `
      <article
        class="blackroll-cart-upsell__product"
        data-upsell-product
        data-product-id="${product.id}"
      >
        <a
          class="blackroll-cart-upsell__image-wrapper"
          href="${productUrl}"
          aria-label="${productTitle}"
        >
          ${imageMarkup}
        </a>

        <div class="blackroll-cart-upsell__information">
          <a
            class="blackroll-cart-upsell__product-title"
            href="${productUrl}"
          >
            ${productTitle}
          </a>

          <span
            class="blackroll-cart-upsell__price"
            data-upsell-price
          >
            ${this.formatMoney(defaultVariant.price)}
          </span>
        </div>

        <div class="blackroll-cart-upsell__action">
          ${actionMarkup}
        </div>
      </article>
    `;
  }

  bindEvents() {
    this.track.addEventListener('change', (event) => {
      const select = event.target.closest(VARIANT_SELECTOR);

      if (!select) {
        return;
      }

      const card = select.closest('[data-upsell-product]');
      const addButton = card?.querySelector(ADD_SELECTOR);
      const priceElement = card?.querySelector('[data-upsell-price]');
      const selectedOption = select.options[select.selectedIndex];

      if (!addButton || !selectedOption?.value) {
        return;
      }

      addButton.dataset.variantId = selectedOption.value;
      addButton.disabled = false;

      if (priceElement && selectedOption.dataset.price) {
        priceElement.textContent = this.formatMoney(
          selectedOption.dataset.price
        );
      }
    });

    this.track.addEventListener('click', (event) => {
      const addButton = event.target.closest(ADD_SELECTOR);

      if (!addButton) {
        return;
      }

      event.preventDefault();
      this.addProduct(addButton);
    });
  }

  async addProduct(button) {
    if (button.disabled) {
      return;
    }

    const variantId = button.dataset.variantId;
    const label = button.querySelector('[data-upsell-button-label]');

    if (!variantId || !label) {
      return;
    }

    button.disabled = true;
    label.textContent = 'Προσθήκη…';

    try {
      const response = await fetch(`${this.rootUrl}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
          sections: ['cart-drawer-section', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          'Το προϊόν δεν μπόρεσε να προστεθεί.'
        );
      }

      button.classList.add('is-added');
      label.textContent = 'Προστέθηκε ✓';

      const card = button.closest('[data-upsell-product]');

      if (card) {
        card.remove();
      }

      const remainingCards = this.track.querySelectorAll(
        '[data-upsell-product]'
      );

      let preservedUpsellMarkup = '';

      if (remainingCards.length) {
        const preservedUpsell = this.element.cloneNode(true);
        preservedUpsell.removeAttribute('data-upsell-initialized');
        preservedUpsellMarkup = preservedUpsell.outerHTML;
      }

      this.publishCartEvents(result);
      await this.refreshCartDrawer(result, preservedUpsellMarkup);
    } catch (error) {
      console.error('BLACKROLL upsell add error:', error);

      button.disabled = false;
      label.textContent = 'Δοκιμάστε ξανά';

      setTimeout(() => {
        label.textContent = 'Προσθήκη';
      }, 1800);
    }
  }

  async refreshCartDrawer(result, preservedUpsellMarkup = '') {
    const renderedSection = result.sections?.['cart-drawer-section'];

    if (!renderedSection) {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
        })
      );
      return;
    }

    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(
      renderedSection,
      'text/html'
    );

    const currentWrapper = document.querySelector(
      '#shopify-section-cart-drawer-section'
    );

    const newWrapper = documentFragment.querySelector(
      '#shopify-section-cart-drawer-section'
    );

    if (!currentWrapper || !newWrapper) {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
        })
      );
      return;
    }

    currentWrapper.innerHTML = newWrapper.innerHTML;

    if (preservedUpsellMarkup) {
      const freshUpsell = currentWrapper.querySelector(UPSELL_SELECTOR);

      if (freshUpsell) {
        freshUpsell.outerHTML = preservedUpsellMarkup;
      }
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: {
          source: 'blackroll-product-upsell',
          data: result,
        },
      })
    );

    requestAnimationFrame(() => {
      initializeBlackrollProductUpsells();
      this.reopenDrawer();
    });
  }

  reopenDrawer() {
    const themeDrawer = document.querySelector('#cart-drawer');
    const dialog = themeDrawer?.querySelector('dialog');

    if (
      dialog &&
      !dialog.open &&
      typeof dialog.showModal === 'function'
    ) {
      try {
        dialog.showModal();
      } catch (error) {
        console.warn('Could not reopen cart drawer dialog:', error);
      }
    }
  }

  publishCartEvents(result) {
    document.dispatchEvent(
      new CustomEvent('cart:add', {
        bubbles: true,
        detail: {
          source: 'blackroll-product-upsell',
          data: result,
        },
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          source: 'blackroll-product-upsell',
          data: result,
        },
      })
    );
  }

  remove() {
    this.element?.remove();
  }
}

function initializeBlackrollProductUpsells() {
  document.querySelectorAll(UPSELL_SELECTOR).forEach((element) => {
    if (element.dataset.upsellInitialized === 'true') {
      return;
    }

    element.dataset.upsellInitialized = 'true';
    new BlackrollProductUpsell(element);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initializeBlackrollProductUpsells
  );
} else {
  initializeBlackrollProductUpsells();
}

document.addEventListener(
  'shopify:section:load',
  initializeBlackrollProductUpsells
);

document.addEventListener('cart:updated', () => {
  requestAnimationFrame(initializeBlackrollProductUpsells);
});

const upsellObserver = new MutationObserver((mutations) => {
  const containsUpsell = mutations.some((mutation) =>
    Array.from(mutation.addedNodes).some((node) => {
      if (!(node instanceof Element)) {
        return false;
      }

      return (
        node.matches?.(UPSELL_SELECTOR) ||
        node.querySelector?.(UPSELL_SELECTOR)
      );
    })
  );

  if (containsUpsell) {
    initializeBlackrollProductUpsells();
  }
});

upsellObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});
